import React, { useState, useCallback, useEffect } from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import logger from "utils/logger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDoubleLeft,
  faAngleLeft,
  faEllipsisH,
  faAngleRight,
  faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons";

const modelVariants = {
  mini: {
    nav: css`
      padding: 5px 0;
    `,
  },
  normal: {
    nav: css`
      padding: 10px 0;
    `,
  },
};

const StyledPaginationNav = styled.nav`
  width: 100%;
  ${({ mode }) => modelVariants[mode]?.nav}
  background-color: ${({ theme }) => theme.secondBackgroundColor};
  box-shadow: ${({ theme }) => theme.boxShadowInner};
  display: flex;
  justify-content: center;
`;

const StyledButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  border: 0;
  outline: none;
  border-top: solid 1px ${({ theme }) => theme.backgroundColor};
  padding: 5px 11px;
  margin: 0 10px;
  color: ${({ theme }) => theme.black};
  transition-property: border-color, color, background-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  ${({ active }) =>
    active
      ? css`
          background-color: ${({ theme }) => theme.darkGray};
          color: ${({ theme }) => theme.white};
        `
      : css`
          :hover {
            border-top: solid 1px ${({ theme }) => theme.dark};
            color: ${({ theme }) => theme.dark};
          }
        `}
`;

const SpaceDots = styled.div`
  width: 50px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8b8b8b;
`;

const PaginationNav = ({
  // 总数
  total,
  // 每页容量
  pageSize,
  //当前页
  currentPage,
  mode = "normal",
  onCurrentPageChange,
}) => {
  const [pageNumArray, setPageNumArray] = useState([]);

  const handlePageButtonClick = useCallback(
    (pageNum) => {
      if (pageNum <= 0) return;
      if (pageNumArray.length !== 0 && pageNum > pageNumArray.length) return;
      onCurrentPageChange && onCurrentPageChange(pageNum);
    },
    [onCurrentPageChange, pageNumArray.length]
  );

  useEffect(() => {
    // 计算共有多少页
    const count = Math.ceil(total / pageSize);
    if (count < 1) return;
    // 生成页码数组
    let start = 1;
    const array = _.range(start, count + 1, 1);
    logger.log("生成页码数组：", array, "数据总数", total);
    setPageNumArray(array);
  }, [pageSize, total, currentPage]);

  // 页码数组为空时 不显示导航
  if (pageNumArray.length === 0 || total < 1) {
    return null;
  }

  return (
    <StyledPaginationNav mode={mode}>
      <StyledButton onClick={() => handlePageButtonClick(1)}>
        <FontAwesomeIcon icon={faAngleDoubleLeft} />
      </StyledButton>
      <StyledButton onClick={() => handlePageButtonClick(currentPage - 1)}>
        <FontAwesomeIcon icon={faAngleLeft} />
      </StyledButton>

      {/* 页码数组 */}
      {pageNumArray.map((num) => {
        if (
          (num === 2 && currentPage > 4) ||
          (num === pageNumArray[pageNumArray.length - 2] &&
            currentPage < pageNumArray[pageNumArray.length - 4])
        )
          return (
            <SpaceDots key={num}>
              <FontAwesomeIcon icon={faEllipsisH} />
            </SpaceDots>
          );
        if (
          (num < currentPage - 2 && num !== 1) ||
          (num > currentPage + 2 &&
            num !== pageNumArray[pageNumArray.length - 1])
        )
          return null;
        return (
          <StyledButton
            key={num}
            active={currentPage === num}
            onClick={() => handlePageButtonClick(num)}
          >
            {num}
          </StyledButton>
        );
      })}

      <StyledButton onClick={() => handlePageButtonClick(currentPage + 1)}>
        <FontAwesomeIcon icon={faAngleRight} />
      </StyledButton>
      <StyledButton onClick={() => handlePageButtonClick(pageNumArray.length)}>
        <FontAwesomeIcon icon={faAngleDoubleRight} />
      </StyledButton>
    </StyledPaginationNav>
  );
};

export default PaginationNav;
